import React, { createContext, ReactNode, useCallback, useMemo, useState } from "react";
import { ICubingService } from "./services/ICubingService";
import { bluetoothCubeService } from "./services/bluetoothCubeService";
import { qiYiCubeService } from "./services/qiyiCubeService";
import { stackmatService } from "./services/stackmatService";
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Backdrop } from "@mui/material";
import { moyu32CubeServicee } from "./services/moyu32CubeService";

export interface ICubingServiceProvider {
  getService: () => ICubingService | null;
  selectService: (type: string) => Promise<void>;
  serviceType: string | null;
}

export const CubeCtx = createContext<ICubingServiceProvider>({
  getService: () => null,
  selectService: async () => {},
  serviceType: null,
});

let staticService: ICubingService | null = null;
let staticServiceType: string | null = null;

export function BluetoothCubeProvider({ children }: { children: ReactNode }) {
  const [selectedService, setSelectedService] = useState<ICubingService | null>(staticService);
  const [serviceType, setServiceType] = useState<string | null>(staticServiceType);
  const [dialogOpen, setDialogOpen] = useState(selectedService === null);

  const selectService = useCallback(async (type: string) => {
    let service: ICubingService;

    switch (type) {
      case "ganbluetooth":
        service = bluetoothCubeService;
        break;
      case "moyubluetooth":
        service = moyu32CubeServicee;
        break;
      case "qiyibluetooth":
        service = qiYiCubeService;
        break;
      case "stackmat":
      default:
        service = stackmatService;
        break;
    }

    await service.init(type);

    staticService = service;
    staticServiceType = type;
    setSelectedService(service);
    setServiceType(type);
    setDialogOpen(false);
  }, []);

  const provider = useMemo(() => ({
    getService: () => staticService,
    selectService,
    serviceType: staticServiceType,
  }), [selectService]);

  return (
    <CubeCtx.Provider value={provider}>
      <Backdrop open={dialogOpen} sx={{ zIndex: 1300 }}>
        <Dialog open={dialogOpen}>
          <DialogTitle>Initialize Cube</DialogTitle>
          <DialogContent>
            Select a cube or timer type to connect and begin timing.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => selectService("ganbluetooth")} variant="contained">Gan</Button>
            <Button onClick={() => selectService("moyubluetooth")} variant="contained">MoYu</Button>
            <Button onClick={() => selectService("qiyibluetooth")} variant="contained">QY Smart</Button>
            <Button onClick={() => selectService("stackmat")} variant="outlined">Stackmat</Button>
          </DialogActions>
        </Dialog>
      </Backdrop>

      {!dialogOpen && children}
    </CubeCtx.Provider>
  );
}
